import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  IconButton,
  Tooltip,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import QualificationApprovalForm from "pages/QualificationsPage/QualificationApprovalForm";
import { useCallback, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  GetQualificationApprovalsRequest,
  IQualification,
  IQualificationApproval,
  QualificationApproval,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";

type QualificationApprovalsListProps = {
  qualification: IQualification;
};
const QualificationApprovalsList = ({
  qualification,
}: QualificationApprovalsListProps) => {
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const { qualificationService } = useGlobalContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE / 2);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("state.stateName");
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    itemToDelete,
    setItemToDelete,
  ] = useState<IQualificationApproval | null>(null);
  const [
    qualificationApprovalToEdit,
    setQualificationApprovalToEdit,
  ] = useState<IQualificationApproval | null>(null);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const { organizationId } = contextStore.useState();

  const DeleteButton = ({ value }: { value: IQualificationApproval }) => {
    const isDisabled = value.organizationId !== organizationId;
    return (
      <Box textAlign="center">
        <Tooltip
          title={
            isDisabled
              ? t(
                  { id: "qualificationApprovalInheritedTooltip" },
                  { organizationName: value.organizationName },
                )
              : ""
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={isDisabled}
              onClick={() => setItemToDelete(value)}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: QualificationApproval }) => {
    const isDisabled = value.organizationId !== organizationId;
    return (
      <Box textAlign="center">
        <Tooltip
          title={
            isDisabled
              ? t(
                  { id: "qualificationApprovalInheritedTooltip" },
                  { organizationName: value.organizationName },
                )
              : ""
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={isDisabled}
              onClick={() => {
                setQualificationApprovalToEdit(value);
                createOpenActions.setTrue();
              }}
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const handleDelete = useCallback(async () => {
    const id = itemToDelete?.id;
    setItemToDelete(null);
    if (id) {
      try {
        await registerPromise(
          qualificationService.deleteQualificationApproval(id),
        );
        queryClient.invalidateQueries([
          "qualificationApprovals",
          qualification.id,
        ]);
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "approvalDeleted" })),
        );
      } catch (error) {
        console.log(error);
      }
    }
  }, [
    itemToDelete?.id,
    registerPromise,
    qualificationService,
    queryClient,
    qualification.id,
    t,
  ]);

  const { isError, data, error, isFetching } = useQuery(
    [
      "qualificationApprovals",
      qualification.id,
      organizationId,
      page,
      rowsPerPage,
      order,
      orderBy,
    ],
    () =>
      qualificationService.getQualificationApprovals(
        qualification.id,
        new GetQualificationApprovalsRequest({
          organizationId: organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        }),
      ),
    {
      keepPreviousData: true,
    },
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const qualificationApproval = data?.data as IQualificationApproval[];

  const cells: Array<TableCellType> = [
    {
      accessor: "stateName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "locationStateLabel" }),
      customSortAccessor: "state.stateName",
    },
    {
      accessor: "professionName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "professionLabel" }),
      customSortAccessor: "profession.name",
    },
    {
      accessor: "minimumCredit",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "minimumCredit" }),
    },
    {
      accessor: "partialCreditIncrement",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "partialCreditIncrement" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const onPartialCreditSave = () => {
    queryClient.invalidateQueries(["qualificationApprovals", qualification.id]);

    createOpenActions.setFalse();
    setQualificationApprovalToEdit(null);

    setSnackbarMessage(
      new SnackbarMessageProps(
        qualificationApprovalToEdit
          ? t({ id: "approvalSaved" })
          : t({ id: "approvalCreated" }),
      ),
    );
  };

  const onPartialCreditCancel = () => {
    createOpenActions.setFalse();
    setQualificationApprovalToEdit(null);
  };

  const titleComponent = (
    <Box display="flex" justifyContent="flex-end" width="100%">
      <Button onClick={createOpenActions.setTrue} startIcon={<AddIcon />}>
        <T id="addApproval" />
      </Button>
    </Box>
  );

  return (
    <Box>
      <Accordion sx={{ mt: 3 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <T id="qualificationApprovals" />
        </AccordionSummary>
        <AccordionDetails>
          <LazyTable
            {...{
              rowCount: data?.totalRecords ?? 0,
              rows: qualificationApproval ?? [],
              loading: isFetching,
              dense: true,
              titleComponent,
              cells,
              page,
              setPage,
              rowsPerPage,
              setRowsPerPage,
              order,
              setOrder,
              orderBy,
              setOrderBy,
            }}
          />
        </AccordionDetails>
      </Accordion>

      <DeleteEntityDialog
        dialogTitleKey="deleteQualificationApproval"
        contentKey="areYouSureToDeleteQualificationApproval"
        open={!!itemToDelete}
        handleDelete={handleDelete}
        onCancel={() => setItemToDelete(null)}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <QualificationApprovalForm
          qualification={qualification}
          qualificationApproval={qualificationApprovalToEdit}
          title={
            qualificationApprovalToEdit !== null
              ? t({ id: "editQualificationApproval" })
              : t({ id: "addQualificationApproval" })
          }
          onSubmit={onPartialCreditSave}
          onCancel={onPartialCreditCancel}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default QualificationApprovalsList;
